import * as XLSX from 'xlsx';
import { Product, ProductFlavor, ProductSize, ProductImage, ProductCollectionType } from '../types';
import { v4 as uuidv4 } from 'uuid';

export interface ProductExcelRow {
  'Product Name / size'?: string;
  'Description'?: string;
  'How many layers available'?: string;
  'Cost & serving size'?: string; // Format: "size:servingSize:price€,size:servingSize:price€"
  'Flavour offered'?: string; // Comma-separated list of flavors with optional prices (format: "Flavor" or "Flavor:price")
  'How many flavors can be selected'?: string; // Number
  'Filling'?: string; // Comma-separated list
  'Cover icing offered'?: string; // Comma-separated list
  'Available cake shape'?: string; // Comma-separated list of shapes
  'Collection'?: string; // One of the valid collection types
  'isActive'?: string; // "true" or "false"
}

// Helper function to validate collection type
const validateCollectionType = (collection: string): ProductCollectionType => {
  const validCollections: ProductCollectionType[] = [
    'Everyday Cakes',
    'Cake & Cupcake Set',
    'Celebration Cakes',
    'Custom Cake Flavours',
    'Create Your Own Cake',
    'Wedding Cakes'
  ];
  
  // Normalize the input collection name (trim and handle case variations)
  const normalizedCollection = collection.trim();
  
  // Check for exact match first
  if (validCollections.includes(normalizedCollection as ProductCollectionType)) {
    return normalizedCollection as ProductCollectionType;
  }
  
  // Check for case-insensitive match
  const matchedCollection = validCollections.find(validCollection => 
    validCollection.toLowerCase() === normalizedCollection.toLowerCase()
  );
  
  if (matchedCollection) {
    return matchedCollection;
  }
  
  // Default to a valid collection type if the provided one is invalid
  return 'Everyday Cakes';
};

export const parseExcelFile = (file: File): Promise<Partial<Product>[]> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const data = e.target?.result;
        const workbook = XLSX.read(data, { type: 'binary' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        
        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json<ProductExcelRow>(worksheet);
        
        // Process each row into a Product object
        const products: Partial<Product>[] = jsonData.map(row => {
          // Process flavors (comma-separated list of flavors with optional prices) - with safety checks
          // Format: "Vanilla", "Chocolate:2.50", "Red Velvet:1.75"
          const flavors: ProductFlavor[] = row['Flavour offered'] != null ? 
            String(row['Flavour offered']).split(',')
              .map(flavor => flavor.trim())
              .filter(flavor => flavor)
              .map(flavor => {
                // Check if flavor has a price (format: "Flavor name:price")
                const flavorParts = flavor.split(':');
                const flavorName = flavorParts[0].trim();
                const flavorPrice = flavorParts.length > 1 ? parseFloat(flavorParts[1]) : undefined;
                
                return {
                  name: flavorName,
                  available: true,
                  price: flavorPrice !== undefined && !isNaN(flavorPrice) ? flavorPrice : 0
                };
              }) : [];
          
          // Process sizes (format: "size:servingSize:price,size:servingSize:price") - with safety checks
          const sizes: ProductSize[] = row['Cost & serving size'] != null ? 
            String(row['Cost & serving size']).split(',')
              .map(size => size.trim())
              .filter(size => size)
              .map(size => {
                const parts = size.split(':');
                const name = parts[0]?.trim() || '';
                const servingSize = parts[1] ? parseInt(parts[1].trim(), 10) || 0 : 0;
                const price = parts[2] ? parseFloat(parts[2].trim()) || 0 : 0;
                return { name, servingSize, price };
              }) : [];
          
          // Process shapes - with safety checks
          const availableShapes = row['Available cake shape'] != null ? 
            String(row['Available cake shape']).split(',')
              .map(shape => shape.trim())
              .filter(shape => ['Round', 'Heart', 'Square', 'Star'].includes(shape)) as Array<'Round' | 'Heart' | 'Square' | 'Star'> : [];
          
          // Validate collection type - with safety check
          const collectionType = row['Collection'] != null ? validateCollectionType(String(row['Collection'])) : 'Everyday Cakes';
          
          // Process additional fields - with safety checks for null/undefined values
          const availableLayers = row['How many layers available'] != null ? 
            String(row['How many layers available']).split(',').map(layer => layer.trim()).filter(Boolean) : 
            [];
            
          // Safely handle "How many flavors can be selected" which might be null/undefined or non-string
          const maxFlavorsSelectable = row['How many flavors can be selected'] != null ? 
            parseInt(String(row['How many flavors can be selected']).trim(), 10) || 1 : 
            1;
            
          const fillings = row['Filling'] != null ? 
            String(row['Filling']).split(',').map(filling => filling.trim()).filter(Boolean) : 
            [];
            
          const coverIcingOptions = row['Cover icing offered'] != null ? 
            String(row['Cover icing offered']).split(',').map(icing => icing.trim()).filter(Boolean) : 
            [];
          
          // Create the product object with optional fields
          const product: Partial<Product> = {
            images: [] as ProductImage[],
            collection: collectionType
          };
          
          // Add optional fields if they exist - with safety checks
          if (row['Product Name / size'] != null) product.name = String(row['Product Name / size']);
          if (row['Description'] != null) product.description = String(row['Description']);
          if (flavors.length > 0) product.flavors = flavors;
          if (sizes.length > 0) product.sizes = sizes;
          if (availableShapes.length > 0) product.availableShapes = availableShapes;
          if (row['isActive'] != null) product.isActive = String(row['isActive']).toLowerCase() === 'true';
          
          // Add new fields - always include them with default values to avoid undefined
          product.availableLayers = availableLayers;
          product.maxFlavorsSelectable = maxFlavorsSelectable;
          product.fillings = fillings;
          product.coverIcingOptions = coverIcingOptions;
          
          // Debug logging to check for undefined values and show pricing
          console.log('🔍 Excel parsing - Product data before return:', {
            name: product.name,
            collection: product.collection,
            availableLayers: product.availableLayers,
            maxFlavorsSelectable: product.maxFlavorsSelectable,
            fillings: product.fillings,
            coverIcingOptions: product.coverIcingOptions,
            flavors: product.flavors?.map(f => ({ name: f.name, price: f.price })),
            sizes: product.sizes,
            availableShapes: product.availableShapes,
            isActive: product.isActive
          });
          
          // Check for undefined values
          Object.entries(product).forEach(([key, value]) => {
            if (value === undefined) {
              console.error(`❌ UNDEFINED VALUE FOUND in Excel parsing: ${key} = ${value}`);
            }
          });
          
          return product;
        });
        
        resolve(products);
      } catch (error) {
        reject(error);
      }
    };
    
    reader.onerror = (error) => {
      reject(error);
    };
    
    reader.readAsBinaryString(file);
  });
};

export const generateExcelTemplate = (): Blob => {
  // Create worksheet with headers
  const ws = XLSX.utils.json_to_sheet([
    {
      'Product Name / size': 'Sample Cake',
      'Description': 'A delicious cake description',
      'How many layers available': '2, 3, 4',
      'Cost & serving size': 'Standard 6":8:65.00€,Standard 8":15:115.00€,Vintage 10":22:160.00€',
      'Flavour offered': 'Vanilla, Red Velvet, Chocolate:2.50, Green, Lemon:1.75',
      'How many flavors can be selected': '2',
      'Filling': 'Vanilla buttercream, Ganache, Cream cheese',
      'Cover icing offered': 'American buttercream, Swiss meringue buttercream',
      'Available cake shape': 'Round, Heart, Square, Star',
      'Collection': 'Everyday Cakes',
      'isActive': 'true'
    }
  ]);
  
  // Set column widths
  const wscols = [
    { wch: 20 }, // Product Name / size
    { wch: 30 }, // Description
    { wch: 25 }, // How many layers available
    { wch: 40 }, // Cost & serving size
    { wch: 30 }, // Flavour offered
    { wch: 30 }, // How many flavors can be selected
    { wch: 30 }, // Filling
    { wch: 30 }, // Cover icing offered
    { wch: 25 }, // Available cake shape
    { wch: 20 }, // Collection
    { wch: 10 }  // isActive
  ];
  
  ws['!cols'] = wscols;
  
  // Create workbook
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Products');
  
  // Generate Excel file
  const excelBuffer = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  return new Blob([excelBuffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
};
